/**
 * ChatScroll Hook
 *
 * Manages scroll behavior for chat message lists:
 * - Initial scroll to bottom (newest messages)
 * - Scroll position restoration when older messages load at top
 * - Auto-scroll for new incoming messages
 * - "New Messages" button visibility
 * - Chat context switching (reset state when changing chats)
 *
 * Load-more is handled by native phx-viewport-top binding.
 */
export const ChatScroll = {
  mounted() {
    this.currentChatId = this.el.id
    this.hasScrolledToBottom = false
    this.isNearBottom = true
    this.prevScrollHeight = 0
    this.prevScrollTop = 0

    // Focus the message input on mount
    const input = document.querySelector(
      '[id^="message-body-input-"]'
    ) as HTMLInputElement | null
    if (input) {
      input.focus()
    }

    // Get new messages button
    this.newMessagesBtn = this.el.parentElement?.querySelector(
      '[data-new-messages-btn]'
    )
    if (this.newMessagesBtn) {
      this.newMessagesBtn.addEventListener('click', () => {
        this.el.scrollTop = this.el.scrollHeight
        this.newMessagesBtn.classList.add('hidden')
      })
    }

    // Track scroll for new messages indicator
    this.handleScroll = () => {
      this.isNearBottom =
        this.el.scrollHeight - this.el.scrollTop - this.el.clientHeight < 150
      if (this.isNearBottom && this.newMessagesBtn) {
        this.newMessagesBtn.classList.add('hidden')
      }
    }

    this.el.addEventListener('scroll', this.handleScroll)

    // A chat open in a hidden tab must stay unread so the push still goes out;
    // the parent LiveView keys its read marking on this report.
    this.handleVisibility = () => {
      this.pushEventTo(this.el, 'chat_visibility', {
        hidden: document.visibilityState === 'hidden'
      })
    }
    document.addEventListener('visibilitychange', this.handleVisibility)
    if (document.visibilityState === 'hidden') {
      this.handleVisibility()
    }

    // Re-pin to bottom when content height grows asynchronously
    // (e.g. <img> in MessageAttachments finishes loading after the
    // initial post-message scroll). Two cases:
    //   1. First-time scroll: content was shorter than the viewport on
    //      mount (so tryScrollToBottom no-op'd), then images finished
    //      loading and pushed the height past clientHeight - drop the
    //      viewport to the bottom and flag scrolled.
    //   2. Sticky-bottom: user is already near the bottom and async
    //      content (image loads, new messages) is growing the stream -
    //      keep pinning to bottom.
    const streamContainer = this.el.querySelector('[phx-update="stream"]')
    if (streamContainer && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (
          !this.hasScrolledToBottom &&
          this.el.scrollHeight > this.el.clientHeight
        ) {
          this.el.scrollTop = this.el.scrollHeight
          this.prevScrollHeight = this.el.scrollHeight
          this.hasScrolledToBottom = true
          return
        }

        if (this.hasScrolledToBottom && this.isNearBottom) {
          this.el.scrollTop = this.el.scrollHeight
        }
      })
      this.resizeObserver.observe(streamContainer)
    }

    // Try to scroll immediately if content is already there
    this.tryScrollToBottom()
  },

  tryScrollToBottom() {
    if (this.hasScrolledToBottom) {
      return
    }

    const streamContainer = this.el.querySelector('[phx-update="stream"]')
    const messageCount = streamContainer?.children.length || 0

    if (messageCount > 0 && this.el.scrollHeight > this.el.clientHeight) {
      // Use double RAF to ensure layout is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.el.scrollTop = this.el.scrollHeight
          this.prevScrollHeight = this.el.scrollHeight
          this.hasScrolledToBottom = true
        })
      })
    }
  },

  beforeUpdate() {
    // Save scroll state before DOM update
    this.prevScrollHeight = this.el.scrollHeight
    this.prevScrollTop = this.el.scrollTop
  },

  updated() {
    const newScrollHeight = this.el.scrollHeight
    const heightDiff = newScrollHeight - this.prevScrollHeight

    // Check if chat changed (hook reused for different chat)
    if (this.el.id !== this.currentChatId) {
      this.currentChatId = this.el.id
      this.hasScrolledToBottom = false
      this.prevScrollHeight = 0
      this.prevScrollTop = 0
      this.tryScrollToBottom()
      return
    }

    // Initial scroll - keep trying until we succeed
    if (!this.hasScrolledToBottom) {
      this.tryScrollToBottom()
      return
    }

    // Older messages loaded at top (user was scrolled near top)
    if (heightDiff > 0 && this.prevScrollTop < 150) {
      // Maintain scroll position so user sees same content
      this.el.scrollTop = this.prevScrollTop + heightDiff
      this.prevScrollHeight = newScrollHeight
      return
    }

    // New message at bottom
    if (heightDiff > 0) {
      const distanceFromBottom =
        this.prevScrollHeight - this.prevScrollTop - this.el.clientHeight

      if (distanceFromBottom < 150) {
        // User was near bottom - auto-scroll to new message
        // Defer to next frame to ensure layout is complete after DOM patch
        requestAnimationFrame(() => {
          this.el.scrollTop = this.el.scrollHeight
        })
      } else if (this.newMessagesBtn) {
        // User scrolled away - show new messages button
        this.newMessagesBtn.classList.remove('hidden')
      }
    }

    this.prevScrollHeight = newScrollHeight
  },

  destroyed() {
    this.el.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('visibilitychange', this.handleVisibility)
    this.resizeObserver?.disconnect()
  }
}
